import type { ApiPromise } from "@polkadot/api";
import type { QueryFunctionContext, QueryObserverOptions } from "@tanstack/query-core";
import type { CareerLiterval, RaceLiterval, RarityLiterval } from "@/types/nftAttribute";
import type { NftInfo } from "../shell/requests";
import type { EquipPicks, MaterialStock } from "./types";

import * as O from 'fp-ts/Option';
import { isLeft } from "fp-ts/Either";
import * as R from 'ramda';

import { getFedCountsByNft, getFedCountsByReceiver, getFedHistory, getFullFedRanking, getMintedOriginShellCounts } from "./requests";

export interface BasicShellInfo {
  owner: string
  ownerDisplayName?: string
  collectionId: number
  nftId: number
  race: RaceLiterval
  rarity: RarityLiterval
  career: CareerLiterval
  rank: number
  feds: number
}

export function queryHasStartedIncubation(api: ApiPromise, collectionId: number, nftId: number) {
  return {
    queryKey: ['pwIncubation.hasStartedIncubation', collectionId, nftId],
    queryFn: async (ctx: any) => {
      const { queryKey: [, collectionId, nftId ]} = ctx as QueryFunctionContext<[
        string, number, number
      ]>
      const result = await api.query.pwIncubation.hasOriginOfShellStartedIncubation(
        [collectionId, nftId]
      )
      return result.toJSON()
    }
  }
}

export function queryFoodByOwners(api: ApiPromise, ownerAddress: string) {
  return {
    queryKey: ['pwIncubation.foodByOwners', ownerAddress],
    queryFn: async (ctx: any) => {
      const { queryKey: [, ownerAddress] } = ctx as QueryFunctionContext<[
        string, string
      ]>
      const result = await api.query.pwIncubation.foodByOwners(ownerAddress)
      if (result.isSome) {
        const payload = result.unwrap()
        const originOfShellsFed: Map<string, number> = new Map
        payload.originOfShellsFed.forEach((value, keys) => {
          originOfShellsFed.set(keys.toJson().toString(), value.toJSON())
        })
        return O.some({
          era: payload.era.toJSON(),
          foodLeft: payload.foodLeft.toJSON(),
          originOfShellsFed,
        })
      }
      return O.none
    }
  }
}

export function queryMintedOriginShellCounts(endpoint: string, ownerAddress: string) {
  return {
    queryKey: ['pwIncubation.mintedOriginShellCounts', ownerAddress],
    queryFn: async (ctx: any) => {
      const { queryKey: [, ownerAddress] } = ctx as QueryFunctionContext<[string, string]>
      const result = await getMintedOriginShellCounts(endpoint, ownerAddress)
      if (isLeft(result)) {
        return 0
      }
      return result.right
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}

export function queryFedCountsByNft(endpoint: string, collectionId: number, nftId: number) {
  return {
    queryKey: ['fedCountsByNft', collectionId, nftId],
    queryFn: async (ctx: any) => {
      const { queryKey: [, collectionId, nftId] } = ctx as QueryFunctionContext<[string, number, number]>
      const result = await getFedCountsByNft(endpoint, collectionId, nftId)
      if (isLeft(result)) {
        return 0
      }
      return result.right
    },
    // staleTime: 1_000 * 60, // 1 mins
  }
}

export function queryFedCountsByReceiver(endpoint: string, ownerAddress?: string) {
  return {
    queryKey: ['queryFedCountsByReceiver', ownerAddress],
    queryFn: async (ctx: any) => {
      const { queryKey: [, ownerAddress] } = ctx as QueryFunctionContext<[string, string?]>
      if (!ownerAddress) {
        return 0
      }
      const result = await getFedCountsByReceiver(endpoint, ownerAddress)
      if (isLeft(result)) {
        return 0
      }
      return result.right 
    },
    refetchInterval: 1_000 * 60, // 1 minutes
  }
}

export function queryFedRanking(endpoint: string, era?: number | null) {
  return {
    queryKey: ['queryFedRanking', era],
    queryFn: async (ctx: any) => {
      const { queryKey: [, era] } = ctx as QueryFunctionContext<[string, number | null | undefined]>
      const result = await getFullFedRanking(endpoint, era)
      if (isLeft(result)) {
        return []
      }
      return result.right
    }
  }
}

export function querySynthesisDataset(collectionId: number, nftId: number) {
  return {
    queryKey: ['synthesisDataset', collectionId, nftId],
    queryFn: async (ctx: any) => {
      const { queryKey: [, collectionId, nftId] } = ctx as QueryFunctionContext<[string, number, number]>
      const resp = await fetch(`/api/synthesis/${collectionId}/${nftId}`)
      const payload = await resp.json()
      if (payload.succeed) {
        return payload.data as {
          nftInfo: NftInfo,
          equipped: EquipPicks[]
        } & MaterialStock
      }
      return null
    },
  }
}

export function queryFedHistory(endpoint: string, nftId: number, pageSize: number = 15) {
  return {
    queryKey: ['fedHistory', nftId, pageSize],
    queryFn: async (ctx: any) => {
      const { queryKey: [, nftId, pageSize] } = ctx as QueryFunctionContext<[string, number, number]>
      const result = await getFedHistory(endpoint, nftId, pageSize)
      if (isLeft(result)) {
        return []
      }
      return result.right
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}